import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { Hidden } from '@spark-web/hidden';
import { Stack } from '@spark-web/stack';
import { ThemeProvider, useTheme } from '@spark-web/theme';
import { useQueryState } from 'nuqs';
import { MetaFunction, useLoaderData, useOutletContext } from 'react-router';

import { Alert, ErrorType } from '~/components/common/alert';
import { DatadogRum } from '~/components/datadog-rum';
import { Disclaimer } from '~/components/disclaimer';
import { MarketplaceInformation } from '~/components/marketplace-information';
import { PartnerDisclaimer } from '~/components/partner-disclaimer';
import { ReviewSelection } from '~/components/quote-review/review-selection';
import { SelectionSummary } from '~/components/quote-review/selection-summary';
import { VendorList } from '~/components/vendors/vendor-list';
import { PARTNER_DISCLAIMER, PARTNER_REQUIREMENTS, PARTNER_THEME } from '~/constants';
import { useVendorsListUrlParams } from '~/hooks/url-params/useVendorsListUrlParams';
import { usePartnerTheming } from '~/hooks/usePartnerTheming';
import AnalyticsProvider from '~/provider/AnalyticsProvider';
import { LeadProvider } from '~/provider/LeadProvider';
import { ModalProvider } from '~/provider/ModalProvider';
import { PromoProvider } from '~/provider/PromoProvider';
import { VendorProvider } from '~/provider/VendorProvider';
import { MarketplaceLead, OutletContextType } from '~/types/marketplace';

import type { loader as marketplaceLoader } from '../../api+/marketplace+';

export { loader, action } from '../../api+/marketplace+';

export const meta: MetaFunction = () => [{ title: 'Get quotes - Brighte Marketplace' }];

function Index() {
  const theme = useTheme();
  const { background } = usePartnerTheming();
  const data = useLoaderData<typeof marketplaceLoader>();

  const parsedUrlParams = useVendorsListUrlParams({ lead: data.lead as MarketplaceLead });
  const { leadIdParam, channel, partner, postcode, categories } = parsedUrlParams;

  const [channelStr] = useQueryState('channel');
  const [postCodeStr] = useQueryState('postcode');

  const isInvalidPartner = channelStr && !partner;
  const isLeadInvalid = leadIdParam && !data.lead;
  const isLeadMissing = isLeadInvalid || (!leadIdParam && !data.lead && categories?.length === 0);
  const isLeadCreationNeeded = partner && isLeadMissing && PARTNER_REQUIREMENTS[partner]?.requireLead;
  const isLeadAllowedToBeCreated = isLeadCreationNeeded && PARTNER_REQUIREMENTS[partner]?.allowLeadCreation;

  const partnerRequirements = partner ? PARTNER_REQUIREMENTS[partner] : null;
  const isLeadFormRequired =
    !data.lead && partnerRequirements?.allowLeadCreation && (postCodeStr === '' || categories?.length <= 0);

  let alertErrorType: ErrorType | null = null;
  if (isInvalidPartner) {
    alertErrorType = 'INVALID_PARTNER';
  } else if (isLeadAllowedToBeCreated) {
    alertErrorType = 'HOUSEHOLD_DETAILS_REQUIRED';
  } else if (isLeadCreationNeeded || !partnerRequirements?.marketplaceEnabled) {
    alertErrorType = 'GENERIC_ERROR';
  } else if (isLeadFormRequired) {
    alertErrorType = 'GENERIC_PARAMS_REQUIRED';
  }

  return (
    <VendorProvider>
      <LeadProvider lead={data.lead as MarketplaceLead}>
        <DatadogRum />

        <AnalyticsProvider>
          <PromoProvider>
            <ModalProvider>
              <Stack
                height="full"
                css={css({
                  [`@media (max-width:  ${theme.breakpoint.desktop}px)`]: {
                    paddingBottom: '100px',
                  },
                })}
              >
                <MarketplaceInformation partner={partner} />

                <Divider />

                <Stack flex={1} css={css({ background })}>
                  {alertErrorType ? (
                    <Alert error={alertErrorType} partner={partner} />
                  ) : (
                    <VendorList
                      categories={data.categories}
                      promotions={data.promotions}
                      brands={data.manufacturerBrands}
                      primaryPromotion={data.primaryPromotion}
                    />
                  )}

                  {partner && partner in PARTNER_DISCLAIMER ? <PartnerDisclaimer partner={partner} /> : null}
                </Stack>

                <Disclaimer partner={partner} />

                <Hidden above="tablet">
                  <Box
                    css={css({
                      position: 'fixed',
                      bottom: 0,
                      right: 0,
                      width: '100%',
                      zIndex: `${theme.elevation.sticky}`,
                      maxHeight: '180px',
                    })}
                  >
                    <ReviewSelection channel={channel} isMobile />
                  </Box>
                </Hidden>

                <SelectionSummary postcode={postcode} categories={categories} channel={channel} />
              </Stack>
            </ModalProvider>
          </PromoProvider>
        </AnalyticsProvider>
      </LeadProvider>
    </VendorProvider>
  );
}

function IndexWithTheme() {
  const { partner } = useOutletContext<OutletContextType>();
  const activeTheme = partner ? PARTNER_THEME[partner] : 'brighte';

  return (
    <ThemeProvider theme={activeTheme}>
      <Index />
    </ThemeProvider>
  );
}

export default IndexWithTheme;
